// Consent boundary (SNOWBUS-ORDINARY-ANALYTICS-BOUNDARY-20261008-001, founder ruling 2026-10-07).
//
// Ordinary first-party product analytics (GA4 page_view, engagement and funnel events) need no opt-in:
// analytics_storage is always granted on a production host. Only ADVERTISING is optional. Google's
// ad_storage / ad_user_data / ad_personalization stay denied by default, after "Reject", and after "Accept"
// alike (no Google advertising is in scope). The Meta / LinkedIn advertising tags (@/lib/ad-pixels,
// founder 13:36 scope) are the one thing "Accept" turns on, and a later refusal turns them off. This
// supersedes the earlier default-denied analytics design (FOUNDER-QA-FOLLOWUP-20260918-008/009).
//
// The stored choice is the ADVERTISING preference, under its own cookie. The legacy
// `sg_consent_analytics` cookie recorded an analytics choice, which is a different question, so it is
// never read here: a legacy "granted" must not be reinterpreted as advertising consent, nor a legacy
// "denied" as an advertising refusal. Returning visitors are simply asked the new question once.
import { nonEssentialSuppressed } from "@repo/ui/lib/signup-age";

const COOKIE_NAME = "sg_consent_advertising";
const COOKIE_MAX_AGE_DAYS = 365;

export type ConsentChoice = "granted" | "denied";

/** Window event dispatched after a fresh "Accept" so a screen already showing something measurable
 *  (ExportCheckout's review cart) re-checks its gates. Product analytics no longer waits on it — those
 *  events are already sent and deduplicated — and it carries nothing; listeners still send only through
 *  @/lib/analytics-event's gates. */
export const CONSENT_GRANTED_EVENT = "sg:analytics-consent-granted";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const row = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : null;
}

/** `null` means no advertising choice has been recorded yet (the banner should show). */
export function getStoredConsent(): ConsentChoice | null {
  // Youth 13-15 (youth spec v1.1): the server-set flag (@repo/ui/lib/signup-age, from the backend's age band) means advertising is refused,
  // whatever this browser recorded before. No stored choice can opt a youth in.
  if (isYouthAdsOff()) return "denied";
  const raw = readCookie(COOKIE_NAME);
  return raw === "granted" || raw === "denied" ? raw : null;
}

/** The server-set youth flag (no age in it): advertising cannot be granted while it is present. */
export function isYouthAdsOff(): boolean {
  return typeof document !== "undefined" && nonEssentialSuppressed(document.cookie);
}

export function setStoredConsent(choice: ConsentChoice): void {
  if (typeof document === "undefined") return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${COOKIE_NAME}=${isYouthAdsOff() ? "denied" : choice}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/** Consent Mode v2 signals. Product analytics storage is always granted; SnowBus runs no Google advertising,
 * so Google's three advertising signals are denied whatever the visitor chose (the Meta / LinkedIn tags
 * follow the choice in @/lib/ad-pixels). */
export function consentSignals() {
  return {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  } as const;
}

/** Pushes a `consent` "update" call to gtag if it has been initialized. No-ops otherwise — the
 *  default consent state (set at gtag init time) already carries the same signals. The choice never
 *  changes them: rejecting advertising must not disable product analytics. */
export function applyConsentUpdate(_choice: ConsentChoice): void {
  if (typeof window === "undefined") return;
  const gtag = (window as { gtag?: (...args: unknown[]) => void }).gtag;
  gtag?.("consent", "update", consentSignals());
}
